import React from 'react';
import { Shield, ArrowLeft, KeyRound } from 'lucide-react';
import { Field, PrimaryButton } from './LoginControls';

const SetupGuide = ({ mfaData }) => (
  <div className="text-left space-y-3 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
    <p className="text-[13px] text-slate-600"><span className="font-semibold text-slate-900">1.</span> Open your authenticator app (Google Authenticator, Authy, Microsoft Authenticator, 1Password).</p>
    <p className="text-[13px] text-slate-600"><span className="font-semibold text-slate-900">2.</span> Scan this QR code:</p>
    {mfaData?.qr_code && (
      <div className="flex justify-center">
        <img src={mfaData.qr_code} alt="Authenticator QR code" className="w-44 h-44 rounded-xl border border-slate-200 bg-white p-2 shadow-sm" data-testid="mfa-qr-code" />
      </div>
    )}
    {mfaData?.manual_secret && (
      <details className="text-slate-500 text-xs">
        <summary className="cursor-pointer hover:text-slate-800 font-medium">Can&apos;t scan? Enter this key manually</summary>
        <code className="mt-2 block bg-white border border-slate-200 rounded-lg px-2 py-2 font-mono text-[#1F47D6] text-center break-all select-all" data-testid="mfa-manual-secret">{mfaData.manual_secret}</code>
        <p className="mt-1.5 text-slate-400">Account: <span className="text-slate-700">{mfaData.account_name}</span> · Issuer: <span className="text-slate-700">{mfaData.issuer}</span></p>
      </details>
    )}
    <p className="text-[13px] text-slate-600"><span className="font-semibold text-slate-900">3.</span> Enter the 6-digit code from your app to finish setup.</p>
  </div>
);

const RecoveryBox = ({ recoveryCode, setRecoveryCode, onSubmit, loading }) => (
  <div className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3.5 space-y-2.5" data-testid="login-recovery-box">
    <p className="text-xs text-amber-900 font-medium flex items-center gap-1.5"><KeyRound className="w-3.5 h-3.5" /> Enter one of your one-time recovery codes (e.g. 1A2B-3C4D-5E6F).</p>
    <input value={recoveryCode} onChange={(e) => setRecoveryCode(e.target.value.toUpperCase())} placeholder="XXXX-XXXX-XXXX"
      className="w-full h-10 rounded-lg border border-amber-200 bg-white text-center font-mono tracking-widest text-slate-900 outline-none focus:border-amber-400" data-testid="login-recovery-code" />
    <button type="button" onClick={onSubmit} disabled={loading || recoveryCode.replace(/-/g, '').length < 12}
      className="w-full h-10 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-[13.5px] font-semibold disabled:opacity-50 transition-colors" data-testid="login-recovery-submit">
      Sign in with recovery code
    </button>
  </div>
);

const MfaStep = ({ mfaData, mfaCode, setMfaCode, onSubmit, onBack, loading, recoveryMode, setRecoveryMode, recoveryCode, setRecoveryCode, onRecoverySubmit }) => (
  <form onSubmit={onSubmit} className="space-y-4">
    <button type="button" onClick={onBack} className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-900 text-[13px] font-medium transition-colors" data-testid="login-back">
      <ArrowLeft className="w-4 h-4" /> Back to login
    </button>

    <div className="text-center">
      <div className="w-12 h-12 mx-auto mb-3 rounded-2xl bg-[#EEF3FF] flex items-center justify-center">
        <Shield className="w-6 h-6 text-[#1F47D6]" strokeWidth={2} />
      </div>
      {mfaData?.setup_required
        ? <><p className="text-slate-900 font-semibold text-[15px] mb-3">Set up your authenticator</p><SetupGuide mfaData={mfaData} /></>
        : <p className="text-slate-500 text-[13.5px]">Enter the 6-digit code from your authenticator app</p>}
    </div>

    <Field
      label="Verification code"
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      value={mfaCode}
      onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
      placeholder="000000"
      maxLength={6}
      inputClassName="text-center text-[24px] font-semibold tracking-[0.45em] h-14 placeholder:text-slate-300"
      required
      autoFocus
      data-testid="login-mfa-code"
    />

    <PrimaryButton loading={loading} loadingLabel="Verifying..." disabled={mfaCode.length !== 6} data-testid="login-mfa-submit">
      <Shield className="w-4 h-4" /> Verify &amp; sign in
    </PrimaryButton>

    <p className="text-center text-slate-400 text-xs">Codes refresh every 30 seconds in your authenticator app</p>
    {!mfaData?.setup_required && !recoveryMode && (
      <button type="button" onClick={() => setRecoveryMode(true)} data-testid="login-use-recovery-code"
        className="w-full text-center text-xs text-slate-500 hover:text-[#1F47D6] underline underline-offset-2 transition-colors">
        Lost your authenticator? Use a recovery code
      </button>
    )}
    {recoveryMode && <RecoveryBox recoveryCode={recoveryCode} setRecoveryCode={setRecoveryCode} onSubmit={onRecoverySubmit} loading={loading} />}
  </form>
);

export default MfaStep;
